import * as React from "react"
import { cn } from "@/lib/utils"

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Optional className for custom styling
   */
  className?: string
  /**
   * Copyright year (defaults to current year)
   */
  year?: number
  /**
   * URL for the legal/terms link
   */
  legalUrl?: string
}

/**
 * Footer Component
 *
 * Displays copyright information and legal links at the bottom of the page.
 *
 * @example
 * ```tsx
 * <Footer />
 * <Footer year={2025} legalUrl="https://anyw3ar.com/pages/terms" />
 * <Footer className="bg-gray-900 py-8" />
 * ```
 */
export const Footer = React.forwardRef<HTMLElement, FooterProps>(
  ({ className, year = new Date().getFullYear(), legalUrl = "https://anyw3ar.com/pages/terms", ...props }, ref) => {
    return (
      <footer
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-2.5",
          className
        )}
        {...props}
      >
        <p className="text-xs text-center text-white font-normal leading-normal">
          ANYW3AR © - {year} -{" "}
          <a
            href={legalUrl}
            className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent transition-all"
            target="_blank"
            rel="noopener noreferrer"
          >
            Legals
          </a>
        </p>
      </footer>
    )
  }
)

Footer.displayName = "Footer"
